/**
 * A tiny subscribable value — the building block of the settings and progress
 * stores. Shaped for React's useSyncExternalStore: `get` returns the same
 * reference until the value changes, and `subscribe` returns an unsubscribe.
 */
export interface Observable<T> {
  get(): T;
  subscribe(listener: () => void): () => void;
}

export interface WritableObservable<T> extends Observable<T> {
  /** Replace the value and notify listeners (no-op when `next` is the current reference). */
  set(next: T): void;
}

export function createObservable<T>(initial: T): WritableObservable<T> {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      if (Object.is(next, value)) return;
      value = next;
      // Snapshot so a listener that unsubscribes during notification doesn't skip others.
      for (const listener of [...listeners]) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
